import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  PhotoIcon,
  XMarkIcon,
  CalendarDaysIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  // Removed checkImageExists - using direct loading with error handling for better performance

  // Process image data from filename
  const processImageData = (filename) => {
    const nameWithoutExt = filename.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '');
    
    // Extract category from filename if present (e.g., "event_2024_image.jpg" -> "event")
    let category = 'general';
    if (nameWithoutExt.includes('event')) category = 'events';
    else if (nameWithoutExt.includes('classroom')) category = 'academics';
    else if (nameWithoutExt.includes('art')) category = 'arts';
    else if (nameWithoutExt.includes('sport')) category = 'sports';
    else if (nameWithoutExt.includes('graduation')) category = 'celebrations';

    // Create a display name
    const displayName = nameWithoutExt
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      id: filename,
      filename: filename,
      name: displayName,
      category: category,
      src: `/assets/images/gallery/${filename}`,
      alt: displayName
    };
  };

  // Load gallery images
  useEffect(() => {
    const loadGalleryImages = () => {
      try {
        // List of actual images in the gallery folder
        const galleryImages = [
          'DSC08142-min.JPG',
          'DSC08186-min.JPG',
          'DSC08207-min.JPG',
          'DSC08208-min.JPG',
          'DSC08219-min.JPG',
          'DSC08249-min.JPG',
          'DSC08256-min.JPG',
          'DSC08281-min.JPG',
          'DSC08329-min.JPG',
          'DSC08363-min.JPG',
          'DSC08373-min.JPG',
          'DSC08378-min.JPG',
          'DSC08387-min.JPG',
          'DSC08390-min.JPG',
          'DSC08416-min.JPG',
          'DSC08425-min.JPG',
          'DSC08443-min.JPG',
          'DSC08460-min.JPG',
          'DSC08472-min.JPG',
          'DSC08483-min.JPG',
          'DSC08498-min.JPG',
          'IMG_5111-min.jpg',
          'IMG_5113-min.jpg',
          'IMG_5114-min.jpg',
          'IMG_5116-min.jpg',
          'IMG_5120-min.jpg',
          'IMG_5121-min.jpg',
          'IMG_5122-min.jpg',
          'IMG_5123-min.jpg',
          'IMG_5124-min.jpg',
          'IMG_5128-min.jpg'
        ];

        // Process all gallery images directly - no need to check existence
        const allImages = galleryImages.map(imageName => processImageData(imageName));

        setImages(allImages);
        setFilteredImages(allImages);
        setLoading(false);
      } catch (error) {
        console.error('Error loading gallery images:', error);
        setLoading(false);
      }
    };

    loadGalleryImages();
  }, []);

  // Filter images based on category
  useEffect(() => {
    let filtered = images;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(image => image.category === selectedCategory);
    }

    setFilteredImages(filtered);
  }, [selectedCategory, images]);

  // Get unique categories
  const categories = ['all', ...new Set(images.map(image => image.category))];

  const categoryLabels = {
    'all': 'All Photos',
    'events': 'School Events',
    'academics': 'Academics',
    'arts': 'Arts & Culture',
    'sports': 'Sports',
    'celebrations': 'Celebrations',
    'general': 'General'
  };

  if (loading) {
    return (
      <main id="main-content" className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#630503] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading gallery...</p>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main id="main-content" className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Visual Stories
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                School <span className="font-bold">Gallery</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Explore memorable moments, achievements, and daily life at P.S. 11X The Highbridge School through our photo collection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Photo Collection
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">Capturing Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Browse through photos organized by categories to explore memorable moments and events.
            </p>
          </div>
          
          <div className="flex justify-center mb-12">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  aria-pressed={selectedCategory === category}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-[#630503] text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {categoryLabels[category] || category}
                </button>
              ))}
            </div>
          </div>

          {/* Results Summary */}
          <div className="text-center">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-[#630503]">{filteredImages.length}</span> photo{filteredImages.length !== 1 ? 's' : ''}
              {selectedCategory !== 'all' && (
                <span> in <span className="font-semibold">{categoryLabels[selectedCategory]}</span></span>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredImages.length === 0 ? (
            <div className="text-center py-20">
              <div className="flex justify-center mb-6">
                <div className="bg-white p-6 rounded-full shadow-lg">
                  <PhotoIcon className="h-16 w-16 text-gray-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">No Photos Yet</h3>
              <p className="text-gray-600 text-lg mb-8">
                {images.length === 0 
                                  ? "Our gallery is being prepared. Check back soon for photos of school events, activities, and memorable moments!"
                : "No photos match your current filter criteria. Try selecting a different category."
                }
              </p>
              {images.length === 0 && (
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200 max-w-2xl mx-auto">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Coming Soon</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-4 w-4 mr-2 text-[#630503]" />
                      School Events
                    </div>
                    <div className="flex items-center">
                      <TagIcon className="h-4 w-4 mr-2 text-[#630503]" />
                      Classroom Activities
                    </div>
                    <div className="flex items-center">
                      <PhotoIcon className="h-4 w-4 mr-2 text-[#630503]" />
                      Student Achievements
                    </div>
                    <div className="flex items-center">
                      <PhotoIcon className="h-4 w-4 mr-2 text-[#630503]" />
                      Art Exhibitions
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${image.alt}`}
                  className="group cursor-pointer bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:scale-105"
                  onClick={() => setSelectedImage(image)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedImage(image); } }}
                >
                  <div className="aspect-w-4 aspect-h-3 bg-gray-100">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-64 bg-gray-200 items-center justify-center">
                      <PhotoIcon className="h-16 w-16 text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="p-4">
                    {/* Categories removed per user request */}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gallery-modal-title"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-5xl max-h-full bg-white rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#630503] text-white px-6 py-4 flex justify-between items-center">
              <h2 id="gallery-modal-title" className="sr-only">Image Viewer</h2>
              <div>
                {/* Categories removed per user request */}
              </div>
              <button
                onClick={() => setSelectedImage(null)}
                aria-label="Close image viewer"
                className="text-white hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 max-h-[80vh] overflow-auto">
              <img 
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="w-full h-auto object-contain"
                style={{ maxHeight: '70vh' }}
                onError={(e) => {
                  // Fallback to a placeholder or hide the image
                  e.target.style.display = 'none';
                  e.target.insertAdjacentHTML('afterend', '<div class="w-full h-64 bg-gray-200 flex items-center justify-center"><span class="text-gray-500">Image not available</span></div>');
                }}
              />
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end items-center">
                <button
                  onClick={() => setSelectedImage(null)}
                  aria-label="Close image viewer"
                  className="bg-[#630503] text-white px-4 py-2 rounded-lg hover:bg-[#630503]/90 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default Gallery; 